import React from 'react';
import {MoreHorizontal} from 'lucide-react';
import styles from '../Home.module.css';

interface PostOptionsMenuProps {
  authorName: string;
  sharing?: boolean;
  deleting?: boolean;
  shareFeedback: 'shared' | 'copied' | null;
  onShare: () => void;
  onRepost?: () => void;
  onDelete?: () => void;
}

const PostOptionsMenu: React.FC<PostOptionsMenuProps> = ({
  authorName, sharing, deleting, shareFeedback, onShare, onRepost, onDelete,
}) => {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const menuId = React.useId();
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  React.useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={styles.postOptionsContainer}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close();
        } else if (open && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []);
          const index = items.indexOf(document.activeElement as HTMLButtonElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
            : index < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1)
            : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items[next]?.focus();
        }
      }}>
      <button ref={triggerRef} type="button" className={styles.postOptionsButton}
        aria-label={`Open options for ${authorName} post`} title="Post options"
        aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(current => !current)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown') {event.preventDefault(); setOpen(true);}
        }}>
        <MoreHorizontal size={22} aria-hidden="true" />
      </button>
      {open ? (
        <div ref={menuRef} id={menuId} role="menu" aria-label="Post options" className={styles.postOptionsMenu}>
          {onRepost ? <button type="button" role="menuitem" className={styles.authorActionMenuItem}
            onClick={() => {close(); onRepost();}}>Repost</button> : null}
          <button type="button" role="menuitem" className={styles.authorActionMenuItem} disabled={sharing}
            onClick={onShare}>{sharing ? 'Sharing...' : shareFeedback === 'shared' ? 'Shared' : shareFeedback === 'copied' ? 'Link copied' : 'Share'}</button>
          {onDelete ? <button type="button" role="menuitem" disabled={deleting}
            className={`${styles.authorActionMenuItem} ${styles.postDeleteMenuItem}`}
            onClick={() => {close(); onDelete();}}>{deleting ? 'Deleting...' : 'Delete post'}</button> : null}
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(PostOptionsMenu);
